import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useApp } from "../../context/AppState";
import {
  STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX,
  getStaticProductPageUpsellSections,
} from "../../constants/product-page-upsell-template";
import CartDrawerUpsellStrip from "../../components/CartDrawer/CartDrawerUpsellStrip";
import AiBrandAttribution from "../ai-recommendation-upsell/components/AiBrandAttribution";
import { isSectionVisible } from "../../../shared/section-visibility.js";
import {
  ackDeselectToParent,
  ackSelectBlockToParent,
  isDeselectBlockMessage,
  parseSelectBlockMessage,
} from "../../utils/preview-selection-messages";
import styles from "./product-page-upsell-editor-preview.module.css";

const MOCK_PRODUCT = {
  title: "365 Oversized Tote Bag",
  subtitle: "Scarab Teal",
  price: "$49.00",
  vendor: "PANGAIA",
  image:
    "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-3_large.png",
  thumbs: [
    "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-3_large.png",
    "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-4_large.png",
    "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-5_large.png",
    "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-6_large.png",
  ],
  colors: ["Scarab Teal", "Black", "Sand"],
  sizes: ["One Size"],
};

const RELATED_PRODUCTS = [
  { title: "Organic Cotton Hoodie", price: "$89.00" },
  { title: "Everyday Canvas Cap", price: "$34.00" },
  { title: "Recycled Tote Mini", price: "$39.00" },
  { title: "Essential Crew Socks", price: "$18.00" },
];

function mergeEditorPartStyle(baseStyle, { isSelected, isHovered }) {
  const next = { ...(baseStyle || {}) };
  if (isSelected) {
    next.outline = "2px solid #2563eb";
    next.outlineOffset = "2px";
  } else if (isHovered) {
    next.outline = "1px dashed #94a3b8";
    next.outlineOffset = "2px";
  }
  return next;
}

export default function ProductPageUpsellEditorPreview({
  popupData,
  popupState,
  chrome = "full",
}) {
  const { appState, isMobile: viewportIsMobile, previewDeviceIsMobile } = useApp();
  const isMobile =
    appState?.is_preview_mode && typeof previewDeviceIsMobile === "boolean"
      ? previewDeviceIsMobile
      : viewportIsMobile;
  const isWidgetChrome = chrome === "widget";

  const activeStep = useMemo(() => {
    const fromState = popupState?.activeStep;
    if (typeof fromState === "number" && !Number.isNaN(fromState)) return fromState;
    if (typeof appState?.activeStep === "number") return appState.activeStep;
    return 0;
  }, [popupState?.activeStep, appState?.activeStep]);

  const sections = useMemo(
    () => getStaticProductPageUpsellSections(popupData, activeStep),
    [popupData, activeStep]
  );

  const headingSection =
    sections[STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_HEADING];
  const productSection =
    sections[STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_PRODUCT];
  const brandSection =
    sections[STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.BRAND_ATTRIBUTION];

  const [selectedPartIndex, setSelectedPartIndex] = useState(
    STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_HEADING
  );
  const [hoveredPartIndex, setHoveredPartIndex] = useState(null);

  useEffect(() => {
    const onMessage = (event) => {
      if (isDeselectBlockMessage(event.data)) {
        setSelectedPartIndex(-1);
        return;
      }
      const parsed = parseSelectBlockMessage(event.data);
      if (!parsed || typeof parsed.index !== "number") return;
      setSelectedPartIndex(parsed.index);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const selectEditorPart = useCallback((index) => {
    setSelectedPartIndex(index);
    ackSelectBlockToParent({ index, subIndex: -1 });
  }, []);

  const getEditorPartProps = useCallback(
    (index) => {
      const isSelected = selectedPartIndex === index;
      const isHovered = hoveredPartIndex === index;
      return {
        role: "button",
        tabIndex: 0,
        "data-editor-part-index": index,
        style: mergeEditorPartStyle(undefined, { isSelected, isHovered }),
        onClick: (e) => {
          e.stopPropagation();
          selectEditorPart(index);
        },
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            selectEditorPart(index);
          }
        },
        onMouseEnter: () => setHoveredPartIndex(index),
        onMouseLeave: () => setHoveredPartIndex(null),
      };
    },
    [hoveredPartIndex, selectEditorPart, selectedPartIndex]
  );

  const showBrand =
    brandSection &&
    isSectionVisible(brandSection);

  if (isWidgetChrome) {
    return (
      <div
        className={`${styles.widgetOnly} ${isMobile ? styles.isMobile : ""}`}
        data-testid="product-page-upsell-preview"
        data-preview-chrome="widget"
        onClick={() => {
          setSelectedPartIndex(-1);
          ackDeselectToParent();
        }}
      >
        <CartDrawerUpsellStrip
          popupData={popupData}
          headingSection={headingSection}
          productSection={productSection}
          activeStep={activeStep}
          isPreviewMode
          isMobile={isMobile}
          embeddedInEditor
          surface="product-page"
          getEditorPartProps={getEditorPartProps}
          headingPartIndex={STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_HEADING}
          productPartIndex={STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_PRODUCT}
        />
        {showBrand ? (
          <div
            className={styles.brandAnchor}
            {...getEditorPartProps(
              STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.BRAND_ATTRIBUTION
            )}
          >
            <AiBrandAttribution
              section={brandSection}
              forceShowAttribution
              forceShowBadge
            />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`${styles.productPageShell} ${isMobile ? styles.isMobile : ""}`}
      data-testid="product-page-upsell-preview"
      onClick={() => {
        setSelectedPartIndex(-1);
        ackDeselectToParent();
      }}
    >
      <header className={styles.storeHeader} aria-hidden="true">
        <div className={styles.storeBrand}>{MOCK_PRODUCT.vendor}</div>
        <nav className={styles.storeNav} aria-hidden="true">
          <span className={styles.storeNavLink}>Shop</span>
          <span className={styles.storeNavLink}>Collections</span>
          <span className={styles.storeNavLink}>About</span>
        </nav>
        <div className={styles.storeIcons} aria-hidden="true">
          <span>Search</span>
          <span>Bag (0)</span>
        </div>
      </header>

      <div className={styles.productPageInner}>
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <span>Home</span>
          <span className={styles.breadcrumbSep}>/</span>
          <span>Bags</span>
          <span className={styles.breadcrumbSep}>/</span>
          <span>{MOCK_PRODUCT.title}</span>
        </nav>

        <section className={styles.productHero} aria-label="Product page preview">
          <div className={styles.gallery}>
            <div className={styles.productImageWrap}>
              <img
                className={styles.productImage}
                src={MOCK_PRODUCT.image}
                alt={MOCK_PRODUCT.title}
              />
            </div>
            <div className={styles.thumbRow}>
              {MOCK_PRODUCT.thumbs.map((src, index) => (
                <div
                  key={src}
                  className={`${styles.thumb} ${index === 0 ? styles.thumbActive : ""}`}
                >
                  <img src={src} alt="" />
                </div>
              ))}
            </div>
          </div>

          <div className={styles.productInfo}>
            <div className={styles.productHeading}>
              <h1 className={styles.productTitle}>{MOCK_PRODUCT.title}</h1>
              <p className={styles.productSubtitle}>{MOCK_PRODUCT.subtitle}</p>
            </div>

            <div className={styles.productPrice}>{MOCK_PRODUCT.price}</div>

            <div className={styles.optionGroup}>
              <span className={styles.optionLabel}>Color</span>
              <div className={styles.optionPills}>
                {MOCK_PRODUCT.colors.map((color, index) => (
                  <span
                    key={color}
                    className={`${styles.optionPill} ${
                      index === 0 ? styles.optionPillActive : ""
                    }`}
                  >
                    {color}
                  </span>
                ))}
              </div>
            </div>

            <div className={styles.optionGroup}>
              <span className={styles.optionLabel}>Size</span>
              <div className={styles.optionPills}>
                {MOCK_PRODUCT.sizes.map((size) => (
                  <span
                    key={size}
                    className={`${styles.optionPill} ${styles.optionPillActive}`}
                  >
                    {size}
                  </span>
                ))}
              </div>
            </div>

            <button type="button" className={styles.addToCartBtn} tabIndex={-1}>
              Add to cart
            </button>

            <div className={styles.upsellAnchor}>
              <CartDrawerUpsellStrip
                popupData={popupData}
                headingSection={headingSection}
                productSection={productSection}
                activeStep={activeStep}
                isPreviewMode
                isMobile={isMobile}
                embeddedInEditor
                surface="product-page"
                getEditorPartProps={getEditorPartProps}
                headingPartIndex={STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_HEADING}
                productPartIndex={STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_PRODUCT}
              />
            </div>

            {showBrand ? (
              <div
                className={styles.brandAnchor}
                {...getEditorPartProps(
                  STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.BRAND_ATTRIBUTION
                )}
              >
                <AiBrandAttribution
                  section={brandSection}
                  forceShowAttribution
                  forceShowBadge
                />
              </div>
            ) : null}

            <div className={styles.productTabs}>
              <div className={styles.tabList}>
                <span className={`${styles.tab} ${styles.tabActive}`}>Description</span>
              </div>
              <p className={styles.tabBody}>
                Spacious everyday tote with reinforced handles. Your upsell appears below
                Add to cart on the live product page.
              </p>
            </div>
          </div>
        </section>

        <section className={styles.relatedSection} aria-hidden="true">
          <h2 className={styles.relatedTitle}>Related products</h2>
          <div className={styles.relatedGrid}>
            {RELATED_PRODUCTS.map((item) => (
              <article key={item.title} className={styles.relatedCard}>
                <div className={styles.relatedImage} />
                <div className={styles.relatedName}>{item.title}</div>
                <div className={styles.relatedPrice}>{item.price}</div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

ProductPageUpsellEditorPreview.propTypes = {
  popupData: PropTypes.object.isRequired,
  popupState: PropTypes.object,
  chrome: PropTypes.oneOf(["full", "widget"]),
};
